import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  TextField,
  TablePagination,
  CircularProgress,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  FormControl,
  InputLabel,
  Stack,
  Box,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import { useSnackbar } from "notistack";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import api from "../api/axios";

const AdminTable = () => {
  const [admins, setAdmins] = useState([]);
  const [locations, setLocations] = useState([]);
  const [agenciesByLocation, setAgenciesByLocation] = useState({});
  const [loadingAgenciesMap, setLoadingAgenciesMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("active");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const locationLabel = (loc) => {
    if (!loc) return "N/A";
    return [loc.city, loc.country].filter(Boolean).join(", ");
  };

  const roleLabel = (roleId) => {
    return Number(roleId) === 1 ? "Admin" : "Agent";
  };

  const fetchAdmins = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/admins", {
        params: { search, page: page + 1, limit: rowsPerPage, status },
      });

      setAdmins(Array.isArray(res.data?.admins) ? res.data.admins : []);
      setTotal(Number(res.data?.total || 0));
    } catch (err) {
      enqueueSnackbar("Failed to load admins", { variant: "error" });
      setAdmins([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, page, rowsPerPage, status, enqueueSnackbar]);

  const fetchLocations = useCallback(async () => {
    try {
      const res = await api.get("/settings/locations");
      setLocations(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      enqueueSnackbar("Failed to load locations", { variant: "error" });
      setLocations([]);
    }
  }, [enqueueSnackbar]);

  const fetchAgenciesForLocation = useCallback(
    async (locationId) => {
      if (!locationId) return;

      const key = String(locationId);
      if (agenciesByLocation[key]) return;

      try {
        setLoadingAgenciesMap((prev) => ({ ...prev, [key]: true }));

        const res = await api.get(`/agencies?location_id=${locationId}`);
        const agencies = Array.isArray(res.data) ? res.data : [];

        setAgenciesByLocation((prev) => ({
          ...prev,
          [key]: agencies,
        }));
      } catch (err) {
        enqueueSnackbar("Failed to load agencies", { variant: "error" });
        setAgenciesByLocation((prev) => ({
          ...prev,
          [key]: [],
        }));
      } finally {
        setLoadingAgenciesMap((prev) => ({ ...prev, [key]: false }));
      }
    },
    [agenciesByLocation, enqueueSnackbar]
  );

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  useEffect(() => {
    const uniqueLocationIds = [
      ...new Set(
        admins
          .map((admin) => admin.location_id)
          .filter((id) => id !== null && id !== undefined && id !== "")
      ),
    ];

    uniqueLocationIds.forEach((locationId) => {
      fetchAgenciesForLocation(locationId);
    });
  }, [admins, fetchAgenciesForLocation]);

  const getAgencyOptions = (locationId, currentAgency) => {
    if (!locationId) return [];

    const list = agenciesByLocation[String(locationId)] || [];

    if (
      currentAgency &&
      currentAgency.id &&
      !list.some((a) => Number(a.id) === Number(currentAgency.id))
    ) {
      return [currentAgency, ...list];
    }

    return list;
  };

  const handleInputChange = (index, field, value) => {
    setAdmins((prev) => {
      const updated = [...prev];
      const current = { ...updated[index] };

      if (field === "location_id") {
        current.location_id = value;
        current.agency_id = "";
        current.agency_name = "";
        updated[index] = current;

        if (value) {
          fetchAgenciesForLocation(value);
        }

        return updated;
      }

      if (field === "agency_id") {
        const currentOptions = agenciesByLocation[String(current.location_id)] || [];
        const selectedAgency = currentOptions.find(
          (a) => Number(a.id) === Number(value)
        );

        current.agency_id = value;
        current.agency_name = selectedAgency?.name || current.agency_name || "";
        updated[index] = current;
        return updated;
      }

      current[field] = value;
      updated[index] = current;
      return updated;
    });
  };

  const handleUpdate = async (adminId, updated) => {
    try {
      if (!updated.location_id) {
        enqueueSnackbar("Location is required", { variant: "warning" });
        return;
      }

      if (!updated.agency_id) {
        enqueueSnackbar("Agency is required", { variant: "warning" });
        return;
      }

      await api.patch(`/admin/admins/${adminId}`, {
        location_id: updated.location_id,
        agency_id: updated.agency_id,
        role_id: updated.role_id,
      });

      enqueueSnackbar("Admin updated successfully", { variant: "success" });
      fetchAdmins();
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to update admin";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const handleDeactivate = async () => {
    if (!selectedAdmin?.id) return;

    try {
      await api.delete(`/admin/admins/${selectedAdmin.id}`);
      enqueueSnackbar("Administrateur désactivé avec succès", { variant: "success" });
      fetchAdmins();
    } catch (err) {
      const msg = err?.response?.data?.error || "Échec de la désactivation de l’administrateur";
      enqueueSnackbar(msg, { variant: "error" });
    } finally {
      setDeactivateDialogOpen(false);
      setSelectedAdmin(null);
    }
  };

  const handleReactivate = async (user) => {
    try {
      await api.patch(`/admin/admins/${user.id}/reactivate`);
      enqueueSnackbar("Administrateur réactivé avec succès", { variant: "success" });
      fetchAdmins();
    } catch (err) {
      const msg = err?.response?.data?.error || "Échec de la réactivation du compte";
      enqueueSnackbar(msg, { variant: "error" });
    }
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();

    autoTable(doc, {
      head: [["Name", "Email", "Location", "Agency", "Role"]],
      body: admins.map((admin) => {
        const location = locations.find(
          (loc) => Number(loc.id) === Number(admin.location_id)
        );

        return [
          admin.name || "",
          admin.email || "",
          location ? locationLabel(location) : "N/A",
          admin.agency_name || "N/A",
          roleLabel(admin.role_id),
        ];
      }),
    });

    doc.save("admins.pdf");
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
        La liste des administrateurs
      </Typography>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ mb: 2 }}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <TextField
          label="Recherche admins"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          fullWidth
        />

        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Statut</InputLabel>
          <Select
            value={status}
            label="Statut"
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="active">Actifs</MenuItem>
            <MenuItem value="inactive">Désactivés</MenuItem>
            <MenuItem value="all">Tous</MenuItem>
          </Select>
        </FormControl>

        <Button variant="outlined" onClick={handleExportPDF}>
          Exporter PDF
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nom</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Localisation</TableCell>
                  <TableCell>Agence</TableCell>
                  <TableCell>Rôle</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {admins.map((admin, idx) => {
                  const currentLocationAgencies = getAgencyOptions(admin.location_id, {
                    id: admin.agency_id,
                    name: admin.agency_name || "Agence actuelle",
                  });

                  const isAgencyLoading = !!loadingAgenciesMap[String(admin.location_id)];

                  return (
                    <TableRow key={admin.id}>
                      <TableCell>{admin.name}</TableCell>
                      <TableCell>{admin.email}</TableCell>

                      <TableCell sx={{ minWidth: 220 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Localisation</InputLabel>
                          <Select
                            value={admin.location_id || ""}
                            label="Localisation"
                            onChange={(e) =>
                              handleInputChange(idx, "location_id", e.target.value)
                            }
                          >
                            {locations.map((loc) => (
                              <MenuItem key={loc.id} value={loc.id}>
                                {locationLabel(loc)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>

                      <TableCell sx={{ minWidth: 220 }}>
                        <FormControl fullWidth size="small" disabled={!admin.location_id}>
                          <InputLabel>Agence</InputLabel>
                          <Select
                            value={admin.agency_id || ""}
                            label="Agence"
                            onChange={(e) =>
                              handleInputChange(idx, "agency_id", e.target.value)
                            }
                          >
                            {currentLocationAgencies.map((agency) => (
                              <MenuItem key={agency.id} value={agency.id}>
                                {agency.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {isAgencyLoading && (
                          <Box sx={{ mt: 0.5 }}>
                            <CircularProgress size={16} />
                          </Box>
                        )}
                      </TableCell>

                      <TableCell sx={{ minWidth: 140 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Rôle</InputLabel>
                          <Select
                            value={admin.role_id}
                            label="Rôle"
                            onChange={(e) =>
                              handleInputChange(idx, "role_id", e.target.value)
                            }
                          >
                            <MenuItem value={1}>Admin</MenuItem>
                            <MenuItem value={2}>Agent</MenuItem>
                          </Select>
                        </FormControl>
                      </TableCell>

                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          {admin.is_active === false ? (
                            <IconButton
                              color="success"
                              title="Réactiver"
                              onClick={() => handleReactivate(admin)}
                            >
                              <RestoreIcon />
                            </IconButton>
                          ) : (
                          <>
                          <IconButton
                            color="primary"
                            onClick={() =>
                              handleUpdate(admin.id, {
                                location_id: admin.location_id,
                                agency_id: admin.agency_id,
                                role_id: admin.role_id,
                              })
                            }
                          >
                            <SaveIcon />
                          </IconButton>

                          <IconButton
                            color="error"
                            onClick={() => {
                              setSelectedAdmin(admin);
                              setDeactivateDialogOpen(true);
                            }}
                          >
                            <BlockIcon />
                          </IconButton>
                          </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {admins.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Aucun administrateur trouvé.
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={total}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPageOptions={[10]}
          />
        </>
      )}

      <Dialog open={deactivateDialogOpen} onClose={() => setDeactivateDialogOpen(false)}>
        <DialogTitle>Confirmer la désactivation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Désactiver l’administrateur <strong>{selectedAdmin?.name}</strong> ? Son historique sera conservé et il ne pourra plus se connecter tant que son compte n’est pas réactivé.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialogOpen(false)}>Annuler</Button>
          <Button color="error" onClick={handleDeactivate}>
            Désactiver
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AdminTable;